import { memo, useMemo } from "react";
import { Box, Button, Grid, Stack, Typography, alpha } from "@mui/material";
import { amber } from "@mui/material/colors";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DeepPartial, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFormContext } from "@4uhub/lib4uhub";

import useCopy from "../../../../../hooks/useCopy";
import { TRegisterForm } from "../RegisterSchema";
import {
  SITE_APP_TYPE_EXTERNAL_SITE,
  isChatProfileEnabled,
} from "../SiteAppType";
import {
  DEFAULT_CHAT_HEIGHT,
  DEFAULT_CHAT_PRIMARY_COLOR,
  DEFAULT_CHAT_SECONDARY_COLOR,
  DEFAULT_CHAT_WIDTH,
} from "./SiteAppChatProfile";

const CHAT_WIDGET_SCRIPT = process.env.REACT_APP_4UCHAT + "/chat-widget.umd.js";

const translationPath = "page.register.applications.";

const getFileUrl = (
  file?: { cdnDomain?: string; filePath?: string; baseUrl?: string } | null
) =>
  file?.cdnDomain && file?.filePath
    ? file.cdnDomain + "/" + file.filePath
    : file?.baseUrl;

const buildEmbedCode = (values: DeepPartial<TRegisterForm>) => {
  const colors = [
    values.chatPrimaryColor &&
      `primary: ${JSON.stringify(values.chatPrimaryColor)}`,
    values.chatSecondaryColor &&
      `secondary: ${JSON.stringify(values.chatSecondaryColor)}`,
  ].filter(Boolean);

  const logoUrl = getFileUrl(values.chatLogo);
  const botAvatarUrl = getFileUrl(values.chatBotAvatar);

  // Campos vazios ficam de fora para o widget usar o padrão dele
  const configLines = [
    `ticketServiceId: ${JSON.stringify(values.chatTicketService?.id ?? "")}`,
    `fullScreenChat: ${!!values.fullScreenMode}`,
    values.chatWidth && `width: ${values.chatWidth}`,
    values.chatHeight && `height: ${values.chatHeight}`,
    colors.length > 0 && `colors: { ${colors.join(", ")} }`,
    values.chatTitle && `title: ${JSON.stringify(values.chatTitle)}`,
    values.chatSubtitle && `subtitle: ${JSON.stringify(values.chatSubtitle)}`,
    values.chatBotName && `botName: ${JSON.stringify(values.chatBotName)}`,
    logoUrl && `logoUrl: ${JSON.stringify(logoUrl)}`,
    botAvatarUrl && `botAvatarUrl: ${JSON.stringify(botAvatarUrl)}`,
  ].filter(Boolean);

  return [
    "<script>",
    "  const fourUHubChatConfig = {",
    configLines.map((line) => `    ${line}`).join(",\n"),
    "  };",
    "  window.fourUHubChatConfig = fourUHubChatConfig;",
    "</script>",
    `<script src="${CHAT_WIDGET_SCRIPT}" defer></script>`,
  ].join("\n");
};

const SiteAppChatEmbedCode = () => {
  const { t } = useTranslation();
  const { copy } = useCopy();
  const { control, formState } = useFormContext<TRegisterForm>();
  const { id } = useParams();

  const [
    type,
    chatTicketService,
    fullScreenMode,
    chatWidth,
    chatHeight,
    chatPrimaryColor,
    chatSecondaryColor,
    chatTitle,
    chatSubtitle,
    chatBotName,
    chatLogo,
    chatBotAvatar,
  ] = useWatch({
    control,
    name: [
      "type",
      "chatTicketService",
      "fullScreenMode",
      "chatWidth",
      "chatHeight",
      "chatPrimaryColor",
      "chatSecondaryColor",
      "chatTitle",
      "chatSubtitle",
      "chatBotName",
      "chatLogo",
      "chatBotAvatar",
    ],
  });

  const code = useMemo(
    () =>
      buildEmbedCode({
        chatTicketService,
        fullScreenMode,
        chatWidth,
        chatHeight,
        chatPrimaryColor,
        chatSecondaryColor,
        chatTitle,
        chatSubtitle,
        chatBotName,
        chatLogo,
        chatBotAvatar,
      }),
    [
      chatTicketService,
      fullScreenMode,
      chatWidth,
      chatHeight,
      chatPrimaryColor,
      chatSecondaryColor,
      chatTitle,
      chatSubtitle,
      chatBotName,
      chatLogo,
      chatBotAvatar,
    ]
  );

  // O que está no site é o script dos valores carregados, com os mesmos
  // padrões de tamanho e cor que a tela preenche quando estão vazios
  const loadedCode = useMemo(() => {
    const loaded = formState.defaultValues ?? {};
    return buildEmbedCode({
      ...loaded,
      chatWidth: loaded.chatWidth || DEFAULT_CHAT_WIDTH,
      chatHeight: loaded.chatHeight || DEFAULT_CHAT_HEIGHT,
      chatPrimaryColor: loaded.chatPrimaryColor || DEFAULT_CHAT_PRIMARY_COLOR,
      chatSecondaryColor:
        loaded.chatSecondaryColor || DEFAULT_CHAT_SECONDARY_COLOR,
    });
  }, [formState.defaultValues]);

  const hasScriptChanges = !!id && code !== loadedCode;

  if (
    !isChatProfileEnabled(type?.code, chatTicketService) ||
    type?.code !== SITE_APP_TYPE_EXTERNAL_SITE
  ) {
    return null;
  }

  return (
    <Grid item xs={12} sx={{ mb: 6 }}>
      <Stack alignItems="flex-start" gap={1}>
        <Typography variant="h6">
          {t(translationPath + "chat_embed_code")}
        </Typography>

        {hasScriptChanges && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 4,
              border: 1,
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? alpha(amber[500], 0.4)
                  : amber[300],
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? alpha(amber[500], 0.15)
                  : amber[50],
              color: (theme) =>
                theme.palette.mode === "dark" ? amber[200] : "#7a5b00",
            }}
          >
            <InfoOutlinedIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>
              {t(translationPath + "chat_embed_update_hint")}
            </Typography>
          </Box>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t(translationPath + "chat_embed_code_hint")}
      </Typography>

      <Typography
        component="pre"
        sx={{
          fontFamily: "monospace",
          fontSize: "0.8rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.grey[900]
              : theme.palette.grey[200],
          color: (theme) =>
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : theme.palette.text.primary,
          padding: 2,
          borderRadius: 1,
          mt: 1,
          mb: 2,
        }}
      >
        {code}
      </Typography>

      <Button variant="outlined" onClick={() => copy(code)}>
        {t(translationPath + "chat_embed_copy")}
      </Button>
    </Grid>
  );
};

export default memo(SiteAppChatEmbedCode);
